import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import axios from "axios";
import { FaUpload, FaPlus, FaSave, FaTrash, FaEye } from "react-icons/fa";

const DocumentTab = forwardRef(({ accountNo, accountNoEdit }, ref) => {
  // Configuration extraction from LocalStorage
  const COMPANY_CODE = localStorage.getItem("companyCode") || "-";
  const API_PATHS = JSON.parse(localStorage.getItem("apiPaths") || "[]");

  const BASE_URL = API_PATHS[0]?.IMG_POST_PATH || "https://cubixweberp.com:213";
  const API_BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
  const DOC_GET_URL = API_PATHS[0]?.IMG_API_PATH || "";
  const SERVER_PATH = API_PATHS[0]?.IMG_SERVERPATH || "";
  const CURRENT_USER = localStorage.getItem("currentUserName") || "admin";

  const DOC_TYPE = "ACCOUNTDOCUMENT";
  const IMAGE_PATH = `${SERVER_PATH}DOCUMENT_UPLOAD`;

  const initialDocRow = {
    slno: null, // Holds the server generated docid
    docID: "",
    doctype: "ACCOUNTDOCUMENT", // Pre-set based on user workflow preference
    docname: "",
    exp_date: "",
    imagepath: "",
  };

  // Single unified list for management
  const [docList, setDocList] = useState([{ ...initialDocRow }]);
  const [uploading, setUploading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);

  // Core Identifier Determination
  const activeAccountNo = accountNoEdit || accountNo;
  const isEditMode = !!accountNoEdit;

  // Lifecycle data synchronization
  useEffect(() => {
    if (isEditMode) {
      fetchDocumentData();
    } else {
      setDocList([{ ...initialDocRow }]);
    }
  }, [accountNoEdit, accountNo]);

  // Expose save function directly to parent layout wrapper
  useImperativeHandle(ref, () => ({
    saveDocuments(autoId) {
      saveDocDetails(isEditMode ? activeAccountNo : autoId);
    },
  }));

  // Fetch document metadata list using ACCDOCLIST API route
  const fetchDocumentData = async () => {
    if (!activeAccountNo) return;
    const apiURL = `${API_BASE_URL}/api/CpaysCount/${COMPANY_CODE}/ACCDOCLIST/${activeAccountNo}/-`;

    try {
      const response = await axios.get(apiURL);
      const serverData = response.data || [];
      console.log("🚀 ~ Document Data Fetched via ACCDOCLIST:", serverData);

      if (serverData.length > 0) {
        const mapped = serverData.map((item) => ({
          slno: item.docid,
          docID: item.docid || "",
          doctype: item.doctype || "",
          docname: item.docname || "",
          exp_date: item.exp_date ? item.exp_date.split("T")[0] : "",
          imagepath: item.imagepath || "",
        }));
        setDocList(mapped);
      } else {
        setDocList([{ ...initialDocRow }]);
      }
    } catch (err) {
      console.error("Error inside fetchDocumentData", err);
    }
  };

  const handleChange = (index, field, value) => {
    setDocList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const onAddRow = () => {
    setDocList((prev) => [...prev, { ...initialDocRow }]);
  };

  const handleDeleteRow = async (index, row) => {
    // 1. If it's a freshly added UI row without a backend ID (slno), just strip it from local state
    if (!row.slno) {
      if (!window.confirm(`Remove this row?`)) return;
      const updated = docList.filter((_, i) => i !== index);
      setDocList(updated.length ? updated : [{ ...initialDocRow }]);
      return;
    }

    // 2. Request confirmation for server-side removal
    if (!window.confirm(`Permanently delete document records from server?`)) return;

    try {
      // Construct the standard payload required by both image deletion endpoints
      const deletePayload = [
        {
          CmpCode: COMPANY_CODE,
          mode: "DELETE",
          docuId: row.slno,
          comments: "ACCDOCLIST",
          user: CURRENT_USER,
        },
      ];

      // API Call 1: Delete from the main API config cluster instance
      console.log("Submitting Delete Request to API Cluster...");
      await axios.post(`${API_BASE_URL}/api/CRMImageDelete`, deletePayload, {
        headers: { "Content-Type": "application/json" },
      });

      // // API Call 2: Delete from the secondary physical image storage server instance
      // console.log("Submitting Delete Request to Physical Image Server...");
      // await axios.post(`${BASE_URL}/api/CRMImageDelete`, deletePayload, {
      //   headers: { "Content-Type": "application/json" },
      // });

      // 3. Synchronize UI by refetching the live active dataset list
      await fetchDocumentData();
      alert("Document records successfully purged from all servers.");

    } catch (error) {
      console.error("Critical error during multi-endpoint record deletion sequence:", error);
      alert("An error occurred while deleting the document from one or more backend repositories.");
    }
  };

  // Precise Workflow Chain Execution 
  const handleFileUpload = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!activeAccountNo) {
      alert("Please ensure the core account identifier is ready before attaching records.");
      return;
    }

    setUploading(true);
    try {
      let targetDocId = docList[index].slno;

      // STEP 1 & 2: If the row doesn't have an established 'docid', submit metadata first
      if (!targetDocId) {
        const rowPayload = [{
          CMPCODE: COMPANY_CODE,
          MODE: "ENTRY",
          account: activeAccountNo,
          docID: "", // Database auto-assigns it
          doctype: docList[index].doctype || "ACCOUNTDOCUMENT",
          docname: docList[index].docname || "",
          exp_date: docList[index].exp_date || null,
          imagepath: "", // Intentionally clean; server maps path during target document code injection
        }];

        console.log("⚡ Step 1: Submitting initial metadata record registration payload:", rowPayload);
        await axios.post(`${API_BASE_URL}/api/CRMAccountDocsReg`, rowPayload, {
          headers: { "Content-Type": "application/json" },
        });

        // Step 2: Instantly fetch tracking logs collection to scrape generated identity tokens
        const refreshURL = `${API_BASE_URL}/api/CpaysCount/${COMPANY_CODE}/ACCDOCLIST/${activeAccountNo}/-`;
        const checkRes = await axios.get(refreshURL);
        const currentSavedItems = checkRes.data || [];

        if (currentSavedItems.length > 0) {
          // Identify the record by extracting the highest/latest generated docid block
          const matchingRecord = currentSavedItems[currentSavedItems.length - 1];
          targetDocId = matchingRecord?.docid;
        }
      }

      if (!targetDocId) {
        throw new Error("Could not retrieve a valid auto-assigned unique docid from database index listing.");
      }

      // STEP 3: Multi-part payload compilation targeting file storage cluster
      const formData = new FormData();
      formData.append("DOC_CODE", String(targetDocId));
      formData.append("DOC_TYPE", DOC_TYPE);
      formData.append("file", file);
      formData.append("cmpcode", COMPANY_CODE);
      formData.append("IMGBASE64", "XX");
      formData.append("IMAGEPATH", IMAGE_PATH);
      formData.append("DESCRIPTION", docList[index].docname || file.name);

      console.log(`🚀 Step 3: Pushing physical file up to server instance. Assigned DOC_CODE identity key ->: ${targetDocId}`);
      await axios.post(`${BASE_URL}/api/Image/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // STEP 4: Synchronize client interface UI state seamlessly with structural record rows
      await fetchDocumentData();

    } catch (err) {
      console.error("Critical error tracing sequential multi-part assignment flow:", err);
      alert("Failed to safely align sequential data uploads to specific key constraints.");
    } finally {
      setUploading(false);
    }
  };

  // General Batch Save/Edit handler for standard text row elements modifications
  const saveDocDetails = async (targetAccountNo) => {
    const contextAccountNo = targetAccountNo || activeAccountNo;
    if (!contextAccountNo) return;

    const filteredRows = docList.filter((row) => row.docname || row.doctype || row.docID || row.imagepath);
    if (filteredRows.length === 0) return;

    const payload = filteredRows.map((row) => ({
      CMPCODE: COMPANY_CODE,
      MODE: row.slno ? "EDIT" : "ENTRY",
      account: contextAccountNo,
      docID: row.slno ? String(row.slno) : (row.docID || ""),
      doctype: row.doctype || "ACCOUNTDOCUMENT",
      docname: row.docname || "",
      exp_date: row.exp_date || null,
      imagepath: row.imagepath || "",
    }));

    try {
      await axios.post(`${API_BASE_URL}/api/CRMAccountDocsReg`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      // alert("Document records successfully updated.");
      await fetchDocumentData();
    } catch (error) {
      console.error("Error tracking structural registry parameters:", error);
    }
  };

  // On-Demand file verification and fetch when View button is triggered
  const handleViewDoc = async (row) => {
    if (!row.slno) {
      alert("No valid server file metadata found for this line item.");
      return;
    }

    try {
      setViewLoading(true);
      const targetUrl = `${API_BASE_URL}/api/cpayscount/${COMPANY_CODE}/image/${row.slno}/${DOC_TYPE}`;
      const response = await axios.get(targetUrl);

      const serverPayload = response.data;
      let remotePath = "";

      if (Array.isArray(serverPayload) && serverPayload.length > 0) {
        remotePath = serverPayload[0]?.Imagepath || serverPayload[0]?.imagepath;
      } else if (serverPayload && typeof serverPayload === "object") {
        remotePath = serverPayload.Imagepath || serverPayload.imagepath;
      }

      if (!remotePath) {
        alert("The server did not return a valid Imagepath track for this document.");
        return;
      }

      const fileName = remotePath.split("\\").pop();
      if (!fileName) return;

      const viewURL = `${DOC_GET_URL}/DOCUMENT_UPLOAD/${fileName}`;
      window.open(viewURL, "_blank");

    } catch (err) {
      console.error("Error fetching live document view target:", err);
      alert("Could not load document resource file link from the backend endpoint.");
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="overflow-x-auto max-h-[400px] overflow-y-auto border border-gray-200 rounded-xl shadow-sm">
        <table className="min-w-full text-sm bg-white">
          <thead className="bg-gray-50 sticky top-0 z-10 border-b border-gray-200">
            <tr>
              {/* <th className="px-4 py-2.5 font-semibold text-gray-600 text-left">Document ID</th>
              <th className="px-4 py-2.5 font-semibold text-gray-600 text-left">Doc Type</th> */}
              <th className="px-4 py-2.5 font-semibold text-gray-600 text-left">Description</th>
              <th className="px-4 py-2.5 font-semibold text-gray-600 text-left">Expiry Date</th>
              <th className="px-4 py-2.5 font-semibold text-gray-600 text-center">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {docList.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50/70 transition-colors">
                {/* <td className="px-4 py-2">
                  <input
                    type="text"
                    value={row.docID}
                    disabled
                    placeholder="Auto-Assigned ID"
                    className="w-full text-sm border-none bg-gray-50 text-gray-400 cursor-not-allowed rounded p-1 focus:outline-none"
                  />
                </td> */}

                {/* <td className="px-4 py-2">
                  <input
                    type="text"
                    value={row.doctype}
                    onChange={(e) => handleChange(i, "doctype", e.target.value)}
                    placeholder="Enter Type"
                    className="w-full text-sm border-none bg-transparent rounded p-1 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </td> */}

                <td className="px-4 py-2">
                  <input
                    type="text"
                    value={row.docname}
                    onChange={(e) => handleChange(i, "docname", e.target.value)}
                    placeholder="Enter Description"
                    className="w-full text-sm border-none bg-transparent rounded p-1 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </td>

                <td className="px-4 py-2">
                  <input
                    type="date"
                    value={row.exp_date}
                    onChange={(e) => handleChange(i, "exp_date", e.target.value)}
                    className="w-full text-sm border-none bg-transparent rounded p-1 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </td>

                <td className="px-4 py-2 text-center">
                  <div className="flex justify-center items-center gap-3">
                    <label className="cursor-pointer flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors">
                      <FaUpload className="mr-1.5 text-xs" />
                      <input
                        type="file"
                        className="hidden"
                        disabled={uploading}
                        onChange={(e) => handleFileUpload(i, e)}
                      />
                      <span className="text-xs">
                        {uploading ? "Processing..." : row.imagepath ? "Change" : "Upload"}
                      </span>
                    </label>

                    {row.slno && (
                      <button
                        type="button"
                        disabled={viewLoading}
                        onClick={() => handleViewDoc(row)}
                        className="text-emerald-600 hover:text-emerald-800 p-1 transition-colors disabled:opacity-50"
                        title="Fetch and Open Document Viewport Link"
                      >
                        <FaEye className="text-sm" />
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => handleDeleteRow(i, row)}
                      className="text-rose-600 hover:text-rose-800 p-1 transition-colors"
                      title="Purge Record Row"
                    >
                      <FaTrash className="text-xs" />
                    </button>
                  </div>

                  {row.imagepath && (
                    <div className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[140px] mx-auto" title={row.imagepath}>
                      {row.imagepath.split("\\").pop()}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <button
          type="button"
          onClick={onAddRow}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg text-xs shadow-sm hover:bg-blue-700 transition"
        >
          <FaPlus className="text-[10px]" /> Add Entry Row
        </button>

        <button
          type="button"
          onClick={() => saveDocDetails(activeAccountNo)}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg text-xs shadow-sm hover:bg-emerald-700 transition"
        >
          <FaSave className="text-[10px]" /> Apply Altered Records
        </button>
      </div>
    </div>
  );
});

export default DocumentTab;